import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import verifyimg from '@/js/images/signup-select.webp';
import makershakrslogo from '@/js/images/blackLogo.svg';
import {
  setUserRoleAction,
  setUserDataAction,
} from '@/js/reduxstore/actions/authActions';
import createApiClient from '@/api/axiosInstance';
import { Loader2 } from 'lucide-react';

const formSchema = z.object({
  company_name: z.string().min(2, 'Company name must be at least 2 characters'),
  role: z.enum(['buyer', 'supplier'], {
    required_error: 'Please select a role',
  }),
});

const RoleSelectScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const token = useSelector((state) => state.authReducer.token);

  const { userData } = useSelector((state) => state.authReducer);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: '',
      role: undefined,
    },
  });

  const onSubmit = async (data) => {
    console.log('🎯 Submitting with following data: ');
    console.log(data);

    setIsLoading(true);
    setError('');
    const type = data.role === 'buyer' ? 'buyer' : 'manufacturer';

    try {
      const response = await apiClient.post(
        '/user-type',
        JSON.stringify({
          company_name: data.company_name,
          user_type: type,
        }),
      );

      const res = response.data;
      console.log('✅ Role selection successful! with data:', res);

      const userDetailsResponse = await apiClient.get('/user-details/get', {
        authRequired: false,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
      });

      // Update Redux state
      dispatch(setUserRoleAction(data.role));
      dispatch(
        setUserDataAction({
          ...userData,
          company_name: data.company_name,
          user_id: userDetailsResponse.data.data.user_id,
          user_type_id: userDetailsResponse.data.data.user_type_id,
          role: data.role,
        }),
      );

      navigate(`/${data.role}/dashboard`);
    } catch (error) {
      const error_message = error?.response?.data?.error_message;
      console.error('Error updating role:', error_message);
      setError(error_message || 'Failed to update role. Please try again.');
    } finally {
      setIsLoading(false);
    }

    // Update Redux state
    // dispatch(setUserRoleAction(data.role));
    // dispatch(
    //   setUserDataAction({
    //     ...userData,
    //     company_name: data.company_name,
    //     role: data.role,
    //   })
    // );
    // setIsLoading(false);
    // navigate(`/${data.role}/register`);
  };

  return (
    <>
      <div className="Half-Split font-roboto flex  justify-center min-h-[calc(100vh-70px)]">
        {/* ---- First Half ---- */}
        <div className="half-1 lg:flex w-1/2 items-center justify-center hidden">
          <div className="flex mr-28 flex-col items-end justify-end transform transition-transform duration-300 lg:scale-90 md:scale-75">
            {/* ----- Left Image ----- */}
            <img
              src={verifyimg}
              alt="role-select"
              width={640}
              className="transform transition-transform duration-300"
            />
            {/* ----- Box UI ----- */}
            <div
              className="piece-box w-[350px] h-[124px] fixed rounded-3xl text-[15px] p-5 text-gray-600 text-center"
              style={{
                background:
                  'linear-gradient(97.27deg, rgba(118, 54, 217, 0.09) 5.17%, rgba(250, 250, 250, 0.6) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.11), rgba(0, 0, 0, 0.12))',
                // Scale down width and height while keeping the box in the same fixed position
                // transform: 'scale(0.9)', // Apply the scale directly to the box
                transformOrigin: 'top left', // Ensures the box scales down from the top right corner
              }}
            >
              Find the perfect manufacturer, get quotes, and manage purchase
              orders effortlessly. Bring your medical device to market faster!
            </div>
          </div>
        </div>

        {/* Second half - Role Selection Form */}
        <div className="relative w-full xl:w-1/2 flex bg-gray-100 items-center overflow-y-auto xl:justify-center justify-center">
          <div className="verification-box flex w-5/6 flex-col justify-center items-center text-center">
            <div className="flex justify-center">
              <img
                src={makershakrslogo}
                className="w-[67px] hidden md:block"
                alt="MakerSharks-logo"
              />
              <h1 className="items-center  md:flex ml-3 font-roboto text-2xl lg:text-3xl font-semibold">
                MakerSharks
              </h1>
            </div>
            <div className="w-4/5">
              <p className="mt-4 text-md lg:text-md text-gray-500 text-center">
                This will help us customize your experience and connect you with
                the right tools, resources, and contacts to grow your business
                efficiently.
              </p>
              <p className="text-md lg:text-md mb-2 lg:mb-8 text-gray-500 text-center"></p>
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}

            <div className="w-full max-w-xl px-6 mt-10">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-5"
                >
                  <FormField
                    control={form.control}
                    name="company_name"
                    render={({ field }) => (
                      <FormItem className="text-left">
                        <FormLabel className="block -mb-1 text-sm font-semibold font-roboto text-gray-500">
                          Company Name
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter your company name"
                            className="focus:ring-0 font-roboto focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple bg-gray-100 border-black/25"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-[#6C3BB7] text-xs mt-1" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem className="text-left">
                        <FormLabel className="block -mb-1 text-sm font-semibold font-roboto text-gray-500">
                          Join us as a
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="grid grid-cols-2 gap-4"
                          >
                            <Label className="flex items-center justify-start border border-black/25 rounded-md py-3 px-4 cursor-pointer">
                              <RadioGroupItem
                                value="buyer"
                                id="buyer"
                                className="cursor-pointer mr-2 border-black/25 text-[#7636D9] focus:ring-[#7636D9]"
                              />
                              <span className="cursor-pointer text-gray-500 font-semibold text-[17px] font-roboto">
                                Buyer
                              </span>
                            </Label>
                            <Label className="flex items-center justify-start border border-black/25 rounded-md py-3 px-4 cursor-pointer">
                              <RadioGroupItem
                                value="supplier"
                                id="supplier"
                                className="cursor-pointer mr-2 border-black/25 text-[#7636D9] focus:ring-[#7636D9]"
                              />
                              <span className="cursor-pointer text-gray-500 font-semibold text-[17px] font-roboto">
                                Manufacturer
                              </span>
                            </Label>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage className="text-[#6C3BB7] text-xs mt-1" />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full bg-darkPurple hover:bg-darkPurple/90 py-3 text-white font-semibold rounded-md mt-6"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Continue'
                    )}
                  </Button>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoleSelectScreen;
